import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';

import Guarded from '@/components/Guarded';
import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import useMe from '@/renderer/utils/useMe';
import { usePageContext } from '@/renderer/utils/usePageContext';

import ConnectTelegram from './components/ConnectTelegram';
import DeleteAccountForm from './components/DeleteAccountForm';
import ExtraEmailRecipients from './components/ExtraEmailRecipients';
import FreePlaysSwitch from './components/FreePlaysSwitch';
import PasswordField from './components/PasswordField';
import PrimaryEmailField from './components/PrimaryEmailField';
import ProfilePhotoField from './components/ProfilePhotoField';
import PushNotifications from './components/PushNotifications';
import Setting from './components/Setting';
import SettingsSection from './components/SettingsSection';
import SupportPushMuteSwitch from './components/SupportPushMuteSwitch';
import WebPushSwitch from './components/WebPushSwitch';

export default SettingsPage;

export function Settings() {
  const [user, setUser] = useMe(true);
  const {
    config: { title },
    urlParsed,
  } = usePageContext();

  // Deep-link support: scroll the target setting into view once it's rendered.
  // We drive this off a ?scrollTo= query param (e.g. /settings?scrollTo=telegram)
  // rather than a #hash because the query survives the logged-out sign-in
  // round-trip (render('/signin') -> reload()), whereas the hash gets dropped.
  // The hash is still honored as a fallback for any external #anchor links.
  // Native scrolling misses these because the page renders client-side after
  // the user loads, so the element doesn't exist yet at navigation time.
  const scrollTo =
    (urlParsed?.search?.scrollTo as string | undefined) ||
    (typeof window !== 'undefined' ? window.location.hash.slice(1) : '');
  useEffect(() => {
    if (!user || !scrollTo) {
      return;
    }
    const el = document.getElementById(scrollTo);
    if (el) {
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [user, scrollTo]);

  if (!user) {
    return <>You must be logged in.</>;
  }

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header title="Account Settings" Icon={Cog6ToothIcon} className="py-2 md:hidden" />
      <div className="overflow-auto">
        <div className="mx-auto max-w-3xl pt-6 sm:pt-20">
          <div className="hidden px-4 sm:block sm:px-6 lg:px-8">
            <div className="pb-8">
              <h1 className="font-bold text-lg text-zinc-900 sm:text-xl dark:text-zinc-200">
                {title}
              </h1>
            </div>
          </div>
          <div className="space-y-5 px-4 sm:px-6 lg:px-8">
            <SettingsSection title="Profile">
              {/* Profile photos are staff/admin-only in v1 (#1999); everyone
                  else keeps the initials avatar and never sees this control. */}
              {(user.role === 'admin' || user.role === 'staff') && (
                <Setting
                  title="Profile photo"
                  description="Replaces your initials avatar across the app."
                >
                  <ProfilePhotoField user={user} setUser={setUser} />
                </Setting>
              )}
              <Setting title="Full name">
                {user?.firstName} {user?.lastName}
              </Setting>
              <Setting
                title="Primary email"
                description="Used to sign in and for receipts."
                id="email"
              >
                <PrimaryEmailField user={user} setUser={setUser} />
              </Setting>
              <Setting title="Password">
                <PasswordField />
              </Setting>
            </SettingsSection>

            <SettingsSection title="Membership & billing">
              <Setting title="Services">
                <a
                  href="/settings/memberships"
                  className="font-semibold text-[#15734A] dark:text-[#34B473]"
                >
                  Manage your memberships →
                </a>
              </Setting>
              <Setting
                title="Account balance"
                description="Applied automatically to your next order."
              >
                <span className="font-mono font-semibold">
                  {(user?.balance || 0).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </span>
              </Setting>
            </SettingsSection>

            <SettingsSection title="Notifications">
              <Setting title="Free plays & promos">
                <FreePlaysSwitch defaultValue={!!user.tags?.includes('free')} />
              </Setting>
              <Setting
                title="Extra email recipients"
                description="Add up to two additional email addresses to receive release warnings."
              >
                <ExtraEmailRecipients user={user} setUser={setUser} />
              </Setting>
              <Setting title="Telegram alerts" id="telegram">
                <ConnectTelegram />
              </Setting>
              <Setting title="Push notifications" id="push">
                <PushNotifications />
              </Setting>
              <Setting title="Browser notifications" id="webpush">
                <WebPushSwitch />
              </Setting>
              {user.role === 'admin' && (
                <Setting title="Support-chat push" id="support-push">
                  <SupportPushMuteSwitch />
                </Setting>
              )}
            </SettingsSection>

            <SettingsSection title="Danger zone" danger>
              <Setting title="Delete account" id="delete-account">
                <div className="space-y-3">
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    Permanently delete your account and personal information. This cannot be undone.
                  </p>
                  <DeleteAccountForm />
                </div>
              </Setting>
            </SettingsSection>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

// Main component
function SettingsPage() {
  return (
    <Guarded>
      <Settings />
    </Guarded>
  );
}
